import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon } from '@panel/commons/lib/Icon/Icon';
import { Spinner } from '@panel/commons/lib/Spinner/Spinner';
import { BodyText, BodyText1Css, ButtonText, Headings } from '@panel/commons/lib/typography';
import { useSelector } from 'react-redux';
import { CompanyPubrSelectors } from '@panel/usuarios/src/store/ducks/myCompany';
import { getMainColor, getNeutralColors } from '@panel/commons/lib/theme';
import { useThemeBreakpoint } from '@panel/leads/src/components/hooks/useResize';
import Tooltip from '@panel/commons/lib/Tooltip/Tooltip';
import { puerSendTrackingInfo } from '@panel/usuarios/src/services/utils/gtmUtils';
import { useLoader } from '@panel/commons/lib/Loader';
import { Modal } from '../modal';
import { useContactOwner } from '../../hooks/useContactOwner';
import { mockAgentDefault } from '../../mockData';

const ContactOwnerContainer = styled.div`
  position: relative;
  height: 60px;
  margin-right: 8px;
`;

const ContactOwnerMenu = styled.div`
  display: flex;
  justify-content: right;
  align-items: center;
  height: 100%;
  gap: 4px;
  cursor: pointer;

  > svg {
    margin: 0px;
  }
  .contactRow {
    display: flex;
    flex-direction: column;
    margin-top: 8px;
    gap: 4px;
  }
`;

const ContactOwnerName = styled(ButtonText).attrs({ size: 200 })`
  font-weight: 400;
  font-size: 16px;
`;

const ActiveLineColor = styled.span`
  height: 2px;
  width: 100%;
  border-radius: 10px;
  background: transparent;

  ${({ active }) =>
    active &&
    css`
      background: ${getMainColor()};
    `}
`;

const ContactOwnerDropDown = styled.div`
  position: absolute;
  top: 60px;
  right: 0px;
  display: inline-block;
  background: ${getNeutralColors('white')};
  min-width: 358px;
  padding: 24px;
  border: 1px solid ${getNeutralColors('grisClaroII')};
  border-radius: 0px 0px 20px 20px;
  animation: dropdown 0.2s ease-in;
  animation-fill-mode: forwards;
  visibility: visible;
  z-index: 100;

  @keyframes dropdown {
    0% {
      opacity: 0;
      transform: translateY(-10%);
    }
    100% {
      opacity: 1;
      transform: translateX(0%);
    }
  }
`;

const ContactOwnerModalWrapper = styled.div`
  * {
    white-space: break-spaces;
  }
`;

const ContactOwnerSpinnerWrapper = styled.div`
  background: ${getNeutralColors('white')};
  border-radius: 20px 20px 0px 0px;
  position: absolute;
  left: 0px;
  top: 0px;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${({ theme }) => theme.v2.breakpoints.Min.tablet} {
    background: ${getNeutralColors('white')};
    border-radius: 20px;
  }

  @media ${({ theme }) => theme.v2.breakpoints.Min.desktop} {
    background: ${getNeutralColors('white')};
    border-radius: 0px 0px 20px 20px;
  }
`;

const OwnerNameWrapper = styled.div`
  border-bottom: 1px solid ${getNeutralColors('grisMedioClaro')};
  padding-bottom: 16px;
  margin-bottom: 8px;
`;

const OwnerName = styled(ButtonText).attrs({ size: 100, as: 'p' })`
  color: ${getNeutralColors('black')};
  display: block;
  margin: 0px;
  padding: 0px;
  user-select: text;
  width: 100%;
`;

const OwnerNameMobile = styled(Headings).attrs({ size: 400 })``;

const OwnerDataWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0px 0px;

  svg {
    margin: 0px 0px 3px 0px;
  }
`;

const OwnerOfficeHours = styled(BodyText).attrs({ size: 200, as: 'p' })`
  color: ${getNeutralColors('grisOscuroAccesible')};
  font-size: 400;
  margin: 0px;
`;

const OwnerData = styled(BodyText).attrs({ size: 100 })`
  display: block;
  padding: 0px 8px;
  cursor: pointer;

  @media ${({ theme }) => theme.v2.breakpoints.Min.desktop} {
    cursor: default;
  }
`;

const OwnerDataLink = styled.a`
  ${BodyText1Css}
  padding: 0px 8px;
  text-decoration: underline;

  &:hover,
  :visited,
  :link,
  :active {
    color: ${getNeutralColors('black')};
  }
`;

const CopyWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  opacity: 0;
  ${({ show }) =>
    show &&
    css`
      opacity: 1;
    `}

  > svg {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ContactOwner = () => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const { agent, fetchedAgent, loadingAgent } = useSelector(CompanyPubrSelectors.getCompanyPubr);
  const { ownerName, ownerEmail, ownerPhone } = agent || { ownerName: null, ownerEmail: null, ownerPhone: null };
  const [copy, setCopy] = useState({ call: false, mail: false });
  const site = process.env.REACT_APP_BUILD_SITE;

  const showCopy = useCallback(type => {
    if (type === 'call') {
      setCopy(prev => {
        return { ...prev, call: !prev.call };
      });
    } else {
      setCopy(prev => {
        return { ...prev, mail: !prev.mail };
      });
    }
  }, []);

  const [showDefault, setShowDefault] = useState(false);
  const [blank, setBlank] = useState(false);
  const { getAgentData } = useContactOwner();

  const { setToggleLoader } = useLoader();

  useEffect(() => {
    if (fetchedAgent && agent && !ownerEmail && !ownerPhone && mockAgentDefault[site]) {
      setShowDefault(true);
    }

    if (fetchedAgent && agent && !ownerName && !ownerEmail && !ownerPhone && !mockAgentDefault[site]) {
      setBlank(true);
    }
  }, [fetchedAgent, agent, ownerName, ownerEmail, ownerPhone, setToggleLoader, site]);

  const openDropdown = useCallback(
    value => {
      if (!fetchedAgent) {
        getAgentData();
      }
      setOpened(value);
      if (opened) {
        puerSendTrackingInfo({ event: 'select_content', item_id: 'Desplegar "Mi Agente"' });
      }
    },
    [fetchedAgent, getAgentData, opened]
  );

  return (
    <ContactOwnerContainer onMouseLeave={() => setOpened(false)}>
      <ContactOwnerMenu onMouseEnter={() => openDropdown(true)}>
        <div className='contactRow'>
          <ContactOwnerName>{t('panel.header.contactOwner.name')}</ContactOwnerName>
          <ActiveLineColor active={opened} />
        </div>
        <Icon name={opened ? 'chevronUp' : 'chevronDown'} size={100} />
      </ContactOwnerMenu>
      {opened && (
        <ContactOwnerDropDown opened={opened}>
          {!blank && (
            <>
              {loadingAgent && (
                <ContactOwnerSpinnerWrapper>
                  <Spinner size='32' lineWidth='4' color={getMainColor()} />
                </ContactOwnerSpinnerWrapper>
              )}
              <OwnerNameWrapper>
                <OwnerName>{showDefault ? t('panel.header.contactOwner.nameDefault') : ownerName}</OwnerName>
                <OwnerOfficeHours border>{t('panel.header.contactOwner.hours')}</OwnerOfficeHours>
              </OwnerNameWrapper>

              {ownerPhone && (
                <OwnerDataWrapper onMouseEnter={() => showCopy('call')} onMouseLeave={() => showCopy('call')}>
                  <Icon name='phoneCall' />
                  <OwnerData>{ownerPhone}</OwnerData>
                  <CopyWrapper show={copy.call} onClick={() => navigator.clipboard.writeText(ownerPhone)}>
                    <Icon name='copy' title={t('panel.header.contactOwner.action')} />
                    <Tooltip text={t('panel.header.contactOwner.action')} />
                  </CopyWrapper>
                </OwnerDataWrapper>
              )}

              {!ownerPhone && showDefault && mockAgentDefault[site].phone && (
                <OwnerDataWrapper onMouseEnter={() => showCopy('call')} onMouseLeave={() => showCopy('call')}>
                  <Icon name='phoneCall' />
                  <OwnerData>{t('panel.header.contactOwner.phoneDefault')}</OwnerData>
                  <CopyWrapper
                    show={copy.call}
                    onClick={() => navigator.clipboard.writeText(t('panel.header.contactOwner.phoneDefault'))}
                  >
                    <Icon name='copy' title={t('panel.header.contactOwner.action')} />
                    <Tooltip text={t('panel.header.contactOwner.action')} />
                  </CopyWrapper>
                </OwnerDataWrapper>
              )}

              {ownerEmail && (
                <OwnerDataWrapper onMouseEnter={() => showCopy('mail')} onMouseLeave={() => showCopy('mail')}>
                  <Icon name='email' />
                  <OwnerData href={`mailto:${ownerEmail}`}>{ownerEmail}</OwnerData>
                  <CopyWrapper show={copy.mail} onClick={() => navigator.clipboard.writeText(ownerEmail)}>
                    <Icon name='copy' title={t('panel.header.contactOwner.action')} />
                    <Tooltip text={t('panel.header.contactOwner.action')} />
                  </CopyWrapper>
                </OwnerDataWrapper>
              )}

              {!ownerEmail && showDefault && mockAgentDefault[site].email && (
                <OwnerDataWrapper onMouseEnter={() => showCopy('mail')} onMouseLeave={() => showCopy('mail')}>
                  <Icon name='email' />
                  <OwnerData href={`mailto:${t('panel.header.contactOwner.emailDefault')}`}>
                    {t('panel.header.contactOwner.emailDefault')}
                  </OwnerData>
                  <CopyWrapper
                    show={copy.mail}
                    onClick={() => navigator.clipboard.writeText(t('panel.header.contactOwner.emailDefault'))}
                  >
                    <Icon name='copy' title={t('panel.header.contactOwner.action')} />
                    <Tooltip text={t('panel.header.contactOwner.action')} />
                  </CopyWrapper>
                </OwnerDataWrapper>
              )}

              {ownerPhone && (
                <OwnerDataWrapper>
                  <Icon name='whatsapp' />
                  <OwnerDataLink target='_blank' href={`https://wa.me/${ownerPhone}`}>
                    {t('panel.header.contactOwner.whatsapp')}
                  </OwnerDataLink>
                </OwnerDataWrapper>
              )}

              {!ownerPhone && showDefault && mockAgentDefault[site].whatsapp && (
                <OwnerDataWrapper>
                  <Icon name='whatsapp' />
                  <OwnerDataLink
                    target='_blank'
                    href={`https://wa.me/${t('panel.header.contactOwner.phoneWADefault')}`}
                  >
                    {t('panel.header.contactOwner.whatsapp')}
                  </OwnerDataLink>
                </OwnerDataWrapper>
              )}
            </>
          )}
        </ContactOwnerDropDown>
      )}
    </ContactOwnerContainer>
  );
};

const ContactOwnerModal = () => {
  const { t } = useTranslation();
  const { agent, fetchedAgent, loadingAgent } = useSelector(CompanyPubrSelectors.getCompanyPubr);
  const { ownerName, ownerEmail, ownerPhone } = agent || { ownerName: null, ownerEmail: null, ownerPhone: null };
  const [breakpoint] = useThemeBreakpoint();
  const { getAgentData } = useContactOwner();
  const sitesDefault = useMemo(() => ['ZPAR', 'IWBR', 'ICBR', 'CMBR', 'DVPE', 'UBPE'], []);
  const site = process.env.REACT_APP_BUILD_SITE;

  const [showDefault, setShowDefault] = useState(false);
  const [blank, setBlank] = useState(false);

  useEffect(() => {
    if (!fetchedAgent) {
      getAgentData();
    }
    if (fetchedAgent && agent && !ownerEmail && !ownerPhone && sitesDefault.includes(site)) {
      setShowDefault(true);
    }
    if (fetchedAgent && agent && !ownerName && !ownerEmail && !ownerPhone && !sitesDefault.includes(site)) {
      setBlank(true);
    }
  }, [agent, fetchedAgent, getAgentData, ownerEmail, ownerName, ownerPhone, site, sitesDefault]);

  return (
    <Modal breakpoint={breakpoint} withoutOutsideClose showTopBar noAnimation>
      <ContactOwnerModalWrapper>
        {!blank && (
          <>
            {loadingAgent && (
              <ContactOwnerSpinnerWrapper>
                <Spinner size='32' lineWidth='4' color={getMainColor()} />
              </ContactOwnerSpinnerWrapper>
            )}
            <OwnerNameWrapper>
              <OwnerNameMobile>{showDefault ? t('panel.header.contactOwner.nameDefault') : ownerName}</OwnerNameMobile>
              <OwnerOfficeHours border>{t('panel.header.contactOwner.hours')}</OwnerOfficeHours>
            </OwnerNameWrapper>

            {ownerPhone && (
              <OwnerDataWrapper
                onClick={() => navigator.clipboard.writeText(ownerPhone || t('panel.header.contactOwner.phoneDefault'))}
              >
                <Icon name='phoneCall' />
                <OwnerDataLink href={`tel:${ownerPhone}}`}>{ownerPhone}</OwnerDataLink>
              </OwnerDataWrapper>
            )}

            {!ownerPhone && showDefault && (
              <OwnerDataWrapper
                onClick={() => navigator.clipboard.writeText(t('panel.header.contactOwner.phoneDefault'))}
              >
                <Icon name='phoneCall' />
                <OwnerDataLink href={`tel:${t('panel.header.contactOwner.phoneDefault')}}`}>
                  {t('panel.header.contactOwner.phoneDefault')}
                </OwnerDataLink>
              </OwnerDataWrapper>
            )}

            {ownerEmail && (
              <OwnerDataWrapper>
                <Icon name='email' />
                <OwnerDataLink href={`mailto:${ownerEmail}`}>{ownerEmail}</OwnerDataLink>
              </OwnerDataWrapper>
            )}
            {ownerPhone && (
              <OwnerDataWrapper>
                <Icon name='whatsapp' />
                <OwnerDataLink target='_blank' href={`https://wa.me/${ownerPhone}`}>
                  {t('panel.header.contactOwner.whatsapp')}
                </OwnerDataLink>
              </OwnerDataWrapper>
            )}

            {!ownerPhone && showDefault && (
              <OwnerDataWrapper>
                <Icon name='whatsapp' />
                <OwnerDataLink target='_blank' href={`https://wa.me/${t('panel.header.contactOwner.phoneWADefault')}`}>
                  {t('panel.header.contactOwner.whatsapp')}
                </OwnerDataLink>
              </OwnerDataWrapper>
            )}
          </>
        )}
      </ContactOwnerModalWrapper>
    </Modal>
  );
};

export { ContactOwner, ContactOwnerModal };
